import { formatExpenseMoney, expenseValidationText } from './expensePresentation';
import { formatPlainDate, formatDateTimeValue } from '../../i18n/travelFormat';
import { useTravelText } from '../../app/TravelLocaleContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import { journeyExpenseApi } from '../../api/journeyExpenseApi';
import { createClientRequestId } from './journeyExpenseModel';
import { coreDraft, corePayload, unwrapCore } from './expenseCoreModel';
import SettlementCards from './SettlementCards';
import { BUDGET_CATEGORY_CODES, budgetCategoryLabel } from '../finance/budgetCategory';

/** 人工付款只記錄使用者已完成的付款事實，不呼叫 Payment。 */
export default function CostClosingPanel({
  journeyId,
  groupTripId,
  identity,
  ledger,
  onChanged
}) {
  const {
    t,
    tf,
    intlLocale
  } = useTravelText();
  const money = (value, currency) => formatExpenseMoney(value, currency, intlLocale);
  const date = value => formatPlainDate(value, intlLocale) || t('travel.expense.s06.dateNotSet');
  const time = value => formatDateTimeValue(value, intlLocale) || t('travel.expense.s06.pending');
  const [closing, setClosing] = useState(null),
    [error, setError] = useState(''),
    [notice, setNotice] = useState(''),
    [busy, setBusy] = useState(false),
    [unknown, setUnknown] = useState(false),
    [retry, setRetry] = useState(false);
  const [editing, setEditing] = useState(null),
    [draft, setDraft] = useState(null),
    [reason, setReason] = useState('');
  const pending = useRef(null),
    lock = useRef(false),
    generation = useRef(0),
    scope = useRef('');
  const key = `${journeyId}:${groupTripId}:${identity}`;
  scope.current = key;
  const load = useCallback(async () => {
    const g = ++generation.current;
    try {
      const result = unwrapCore(await journeyExpenseApi.closing(journeyId, groupTripId));
      if (g === generation.current && scope.current === key) {
        setClosing(result);
        setError('');
      }
      return result;
    } catch (e) {
      if (g === generation.current && scope.current === key) {
        setClosing(null);
        setError("travel.expense.s06.unableToLoadSettlementRecordsCheckAccess");
      }
      return null;
    }
  }, [journeyId, groupTripId, key]);
  useEffect(() => {
    setClosing(null);
    setEditing(null);
    setDraft(null);
    setUnknown(false);
    setRetry(false);
    setBusy(false);
    lock.current = false;
    pending.current = null;
    load();
    return () => {
      generation.current += 1;
    };
  }, [load]);
  useEffect(() => {
    load();
  }, [load, ledger.budgetRowVersion]);
  const label = id => ledger.travelers.find(x => String(x.travelerId) === String(id))?.label || tf("travel.expense.s06.traveler", {
    value0: id
  });
  const send = async (kind, input, original = false) => {
    if (lock.current || (!original && unknown) || !closing) return;
    const body = original ? pending.current.body : {
      ...input,
      expectedBudgetRowVersion: closing.budgetRowVersion,
      expectedBasisHash: closing.basisHash,
      clientRequestId: createClientRequestId()
    };
    pending.current = {
      kind,
      body
    };
    const current = key;
    lock.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    setRetry(false);
    try {
      await journeyExpenseApi.closingExecute(journeyId, groupTripId, kind, body);
      if (scope.current !== current) return;
      pending.current = null;
      setUnknown(false);
      setEditing(null);
      setDraft(null);
      setNotice("travel.expense.s06.accountingRecordSavedNoPlatformPaymentWas");
      await load();
      await onChanged();
    } catch (e) {
      if (scope.current !== current) return;
      const status = e?.response?.status;
      if (!status || status >= 500) {
        setUnknown(true);
        setError("travel.expense.s06.unknownResultCheckTheOriginalRequestFirst");
      } else {
        pending.current = null;
        setUnknown(false);
        setError(status === 409 ? "travel.expense.s06.theVersionOrSettlementBasisChangedReload" : status === 403 ? "travel.expense.s06.youDoNotHaveAccountingPermission" : "travel.expense.s06.requestRejectedCheckPrecisionOutstandingAmountActual");
        setClosing(null);
      }
    } finally {
      if (scope.current === current) {
        lock.current = false;
        setBusy(false);
      }
    }
  };
  const reconcile = async () => {
    if (lock.current || !pending.current) return;
    const current = key;
    lock.current = true;
    setBusy(true);
    try {
      const response = await journeyExpenseApi.closingLookup(journeyId, groupTripId, pending.current.body.clientRequestId);
      const envelope = response?.data;
      const found = envelope && Object.prototype.hasOwnProperty.call(envelope, 'data') ? envelope.data : envelope;
      if (scope.current !== current) return;
      if (found) {
        pending.current = null;
        setUnknown(false);
        setRetry(false);
        setNotice("travel.expense.s06.originalOperationVerifiedNoDuplicatePaymentOr");
        await load();
        await onChanged();
      } else {
        setRetry(true);
        setError("travel.expense.s06.noCommittedResultFoundYetYouMay");
      }
    } catch (e) {
      if (scope.current === current) setError("travel.expense.s06.verificationFailedTheResultRemainsUnknownCheck");
    } finally {
      if (scope.current === current) {
        lock.current = false;
        setBusy(false);
      }
    }
  };
  const revise = entry => {
    setEditing(entry);
    setDraft(coreDraft(entry, String(entry.core.dayId)));
    setReason('');
  };
  const setDraftValue = (name, value) => setDraft(x => ({
    ...x,
    [name]: value
  }));
  const toggle = (type, id, checked) => setDraft(x => ({
    ...x,
    [type]: checked ? [...x[type], {
      travelerId: String(id),
      amount: '0'
    }] : x[type].filter(r => r.travelerId !== String(id))
  }));
  const lineAmount = (type, id, value) => setDraft(x => ({
    ...x,
    [type]: x[type].map(r => r.travelerId === String(id) ? {
      ...r,
      amount: value
    } : r)
  }));
  return <section lang={intlLocale} aria-busy={busy} aria-label={t("travel.expense.s06.manualSettlementAndRevisions")}><h2>{t("travel.expense.s06.manualPaymentsAndExpenseRevisions")}</h2>
    {busy&&<p role="status">{t('travel.expense.s06.busy')}</p>}
    {error && <p role="alert">{t(error)}</p>}{notice && <p role="status">{t(notice)}</p>}
    {unknown ? <><button disabled={busy} onClick={reconcile}>{t("travel.expense.s06.checkOriginalOperationResult")}</button>{retry && <button disabled={busy} onClick={() => send(pending.current.kind, null, true)}>{t("travel.expense.s06.retryWithOriginalKey")}</button>}</> : !closing?<button disabled={busy} onClick={load}>{t("travel.expense.s06.reloadSettlementRecords")}</button>:null}
    {!closing&&!error&&<p role="status">{t('travel.expense.s06.loadingClosing')}</p>}
    {closing && <>
      {closing.reconfirmRequired && <p role="status">{t("travel.expense.s06.aRevisionChangedTheSettlementBasisActual")}</p>}
      {closing.reconfirmRequired && closing.canEdit && <button disabled={busy || unknown} onClick={() => send('BASIS_ACK', {
        actualPaymentConfirmed: true
      })}>{t("travel.expense.s06.acknowledgeRevisedBalancesAndPreservedPayments")}</button>}
      {closing.revisions.some(r=>r.state==='PENDING_CONFIRMATION')&&<p role="status">{t('travel.expense.s06.pendingRevisionNotice')}</p>}
      <SettlementCards closing={closing} scale={ledger.minorUnits} label={label} busy={busy} unknown={unknown} onRecord={input => send('SETTLEMENT', input)} /><details><summary>{t('travel.expense.s06.settlementData')}</summary><p>{t('travel.expense.s06.recordOnlyCompletedActualPaymentsThisIs')}</p>{!unknown&&<button disabled={busy} onClick={load}>{t('travel.expense.s06.reloadSettlementRecords')}</button>}<ul>{closing.balances.map(b => <li key={b.travelerId}>{tf("travel.expense.s06.originalNetRemainingBalance", {
            value0: label(b.travelerId),
            value1: money(b.rawNet, closing.currency),
            value2: money(b.remaining, closing.currency),
            value3: closing.currency
          })}</li>)}</ul></details>
      <h3>{t('travel.expense.s06.recordedPayments')}</h3>{closing.settlements.length===0&&<p>{t('travel.expense.s06.emptyPayments')}</p>}<ul>{closing.settlements.map(s => <li key={s.id}>{tf("travel.expense.s06.utcRecordedBy", {
            value0: label(s.payerTravelerId),
            value1: label(s.payeeTravelerId),
            value2: money(s.amount, s.currency),
            value3: s.currency,
            value4: time(s.paidAtUtc),
            value5: s.actorMemberId
          })}</li>)}</ul>
      <details className="expense-core__advanced"><summary>{t("travel.expense.s06.expenseRevisions")}</summary>{closing.canEdit && ledger.entries.filter(e => e.core.stage === 'CONFIRMED').map(e => <button disabled={busy || unknown} key={e.expenseId} onClick={() => revise(e)}>{tf("travel.expense.s06.revise", {
          value0: e.title
        })}</button>)}
      {editing && draft && closing.canEdit && <form onSubmit={e => {
        e.preventDefault();
        try {
          const proposed = corePayload(draft, ledger, 'revision-content');
          send('REVISION_PROPOSED', {
            expenseId: editing.expenseId,
            reason,
            proposed
          });
        } catch (err) {
          setError(expenseValidationText(err, t, tf));
        }
      }}><fieldset disabled={busy || unknown}><legend>{t("travel.expense.s06.proposeARevisionOfficialDebtStaysUnchanged")}</legend>
        <label>{t("travel.expense.s06.revisionReason")}<input required maxLength={500} value={reason} onChange={e => setReason(e.target.value)} /></label>
        <label>{t("travel.expense.s06.name")}<input required value={draft.title} onChange={e => setDraftValue('title', e.target.value)} /></label>
        <details className="expense-core__advanced"><summary>{t('travel.expense.s06.revisionAdvanced')}</summary>        {['baseAmount', 'serviceFee', 'discount'].map((name, i) => <label key={name}>{[t("travel.expense.s06.baseAmount"), t("travel.expense.s06.serviceFee"), t("travel.expense.s06.discount")][i]}<input inputMode="decimal" required value={draft[name]} onChange={e => setDraftValue(name, e.target.value)} /></label>)}
        <label>{t("travel.expense.s06.tripDay")}<select value={draft.dayId} onChange={e => setDraftValue('dayId', e.target.value)}>{ledger.days.map(d => <option key={d.dayId} value={d.dayId}>{date(d.dayDate)}</option>)}</select></label>
        <label>{t("travel.expense.s06.actualExpenseTimeJourneyTimeZone")}<input required type="datetime-local" value={draft.occurredLocalAt} onChange={e => setDraftValue('occurredLocalAt', e.target.value)} /></label>
        <label>{t("travel.expense.s06.category")}<select required value={draft.categoryCode} onChange={e => setDraftValue('categoryCode', e.target.value)}>{BUDGET_CATEGORY_CODES.map(c => <option key={c} value={c}>{budgetCategoryLabel(c, t)}</option>)}</select></label>
        <label>{t("travel.expense.s06.note")}<input value={draft.note} onChange={e => setDraftValue('note', e.target.value)} /></label>
        <label>{t("travel.expense.s06.sharingMethod")}<select value={draft.method} onChange={e => setDraftValue('method', e.target.value)}><option value="EQUAL">{t("travel.expense.s06.equal")}</option><option value="EXACT_AMOUNT">{t("travel.expense.s06.exactAmounts")}</option></select></label>
        {['payments', 'shares'].map(type => <div key={type}><h4>{type === 'payments' ? t("travel.expense.s06.paymentDetails") : t("travel.expense.s06.actualSharesZeroAllowed")}</h4>{ledger.travelers.map(t => <label key={t.travelerId}><input type="checkbox" checked={draft[type].some(x => x.travelerId === String(t.travelerId))} onChange={e => toggle(type, t.travelerId, e.target.checked)} />{t.label}{draft[type].some(x => x.travelerId === String(t.travelerId)) && (type === 'payments' || draft.method === 'EXACT_AMOUNT') && <input aria-label={tf(type === 'payments' ? 'travel.expense.s06.paymentAmount' : 'travel.expense.s06.shareAmount', {
                value0: t.label
              })} inputMode="decimal" value={draft[type].find(x => x.travelerId === String(t.travelerId)).amount} onChange={e => lineAmount(type, t.travelerId, e.target.value)} />}</label>)}</div>)}
</details>        <button>{t("travel.expense.s06.saveRevisionForConfirmation")}</button><button type="button" onClick={() => {
            setEditing(null);
            setDraft(null);
          }}>{t("travel.expense.s06.cancelEditing")}</button>
      </fieldset></form>}
      {closing.revisions.length===0&&<p>{t('travel.expense.s06.emptyRevisions')}</p>}<ul>{closing.revisions.map(r => <li key={r.id}><h4>{tf("travel.expense.s06.revision", {
              value0: r.version.toLocaleString(intlLocale),
              value1: r.state === 'PENDING_CONFIRMATION' ? t("travel.expense.s06.awaitingReconfirmation") : r.state === 'CONFIRMED' ? t("travel.expense.s06.confirmed") : t("travel.expense.s06.cancelled")
            })}</h4><p>{r.reason}</p><p>{tf("travel.expense.s06.nameAmount", {
              value0: r.previous.title,
              value1: r.proposed.title,
              value2: money(r.previous.core.baseAmount, r.previous.core.currency),
              value3: money(r.proposed.core.baseAmount, r.proposed.core.currency)
            })}</p>
        <details><summary>{t("travel.expense.s06.fullPaymentAndShareDifferences")}</summary>{[r.previous, r.proposed].map((v, i) => <div key={i}><h5>{i ? t("travel.expense.s06.afterRevision") : t("travel.expense.s06.beforeRevision")}</h5><p>{tf("travel.expense.s06.feeDiscount", {
                  value0: time(v.occurredAt),
                  value1: v.core.currency,
                  value2: money(v.core.serviceFee, v.core.currency),
                  value3: money(v.core.discount, v.core.currency)
                })}</p>{['payments', 'shares'].map(type => <p key={type}>{tf('travel.expense.s06.lineMoney',{role:t(type==='payments'?'travel.expense.s06.payment':'travel.expense.s06.share'),amount:new Intl.ListFormat(intlLocale,{style:'long',type:'conjunction'}).format(v.core[type].map(x=>tf('travel.expense.s06.personMoney',{person:label(x.travelerId),amount:money(x.amount,v.core.currency)})))})}</p>)}</div>)}</details>
        {r.state === 'PENDING_CONFIRMATION' && closing.canEdit && <><button disabled={busy || unknown} onClick={() => send('REVISION_CONFIRMED', {
              revisionId: r.id,
              actualPaymentConfirmed: true
            })}>{t("travel.expense.s06.confirmRevisionAndRecalculateDebt")}</button><button disabled={busy || unknown} onClick={() => send('REVISION_CANCELLED', {
              revisionId: r.id
            })}>{t("travel.expense.s06.cancelRevisionRetainAudit")}</button></>}
      </li>)}</ul></details>
    </>}
  </section>;
}
